<template>
  <div v-if="isVisible" class="alert-smart-event-modal modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header del Modal -->
      <div class="modal-header" :class="`modal-header-${eventType}`">
        <div class="header-left">
          <h2 class="modal-title">{{ getEventTitle() }}</h2>
          <p class="event-subtitle">{{ getEventSubtitle() }}</p>
        </div>
        
        <button class="close-button" @click="closeModal" :title="$ml.get('close')">
          <span class="mdi mdi-close"></span>
        </button>
      </div>

      <!-- Descripción del Evento (en el header) -->
      <div class="event-description" :class="`event-description-${eventType}`">
        <div class="description-item">
          <label>{{ $ml.get('start_time') }}:</label>
          <span>{{ formatDateTime(eventData?.start) }}</span>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('end_time') }}:</label>
          <span>{{ formatDateTime(eventData?.end) }}</span>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('duration') }}:</label>
          <span>{{ formatDuration(eventData?.start && eventData?.end ? eventData.end.getTime() - eventData.start.getTime() : 0) }}</span>
        </div>
        <div class="description-item">
          <label>{{ $ml.get('events_count') }}:</label>
          <span>{{ getEventCount() }}</span>
        </div>
      </div>

      <!-- Contenido del Modal -->
      <div class="modal-content" :class="`modal-content-${eventType}`">
        <!-- Video del Evento -->
        <div class="video-section">
          <h3>{{ $ml.get('video_event') }}</h3>
          <div class="video-container">
            <video 
              v-if="videoData && videoData.videoUrl"
              ref="eventVideo"
              :src="videoData.videoUrl"
              controls
              class="event-video"
              @loadeddata="onVideoLoaded"
              @error="onVideoError"
              @timeupdate="onVideoTimeUpdate"
            >
              {{ $ml.get('browser_not_support_video') }}
            </video>
            <canvas
              v-if="videoData && videoData.videoUrl"
              ref="bboxCanvas"
              class="bbox-canvas"
            ></canvas>
            <div v-else-if="videoLoading" class="video-loading">
              <span class="mdi mdi-loading mdi-spin"></span>
              <p>{{ $ml.get('loading_video') }}</p>
            </div>
            <div v-else-if="videoError" class="video-error">
              <span class="mdi mdi-alert"></span>
              <p>{{ $ml.get('video_load_error') }}</p>
            </div>
            <div v-else class="no-video">
              <span class="mdi mdi-video-off"></span>
              <p>{{ $ml.get('no_video_available') }}</p>
            </div>
          </div>
        </div>

        <!-- Información de los Eventos - Tabla (Counter: dos tablas; resto: una tabla) -->
        <div class="events-table-section" :class="{ 'events-tables-counter': eventType === 'counter' }">
          <template v-if="eventType === 'counter'">
            <!-- Event Information: #, START, END, EVIDENCIA -->
            <div class="events-table-block">
              <h3>{{ $ml.get('event_information') }}</h3>
              <div class="table-container">
                <div class="table-wrapper">
                  <table class="events-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $ml.get('start') }}</th>
                        <th>{{ $ml.get('end') }}</th>
                        <th>{{ $ml.get('evidence') }}</th>
                      </tr>
                    </thead>
                  </table>
                  <div class="table-body-scroll">
                    <table class="events-table">
                      <tbody>
                        <tr v-if="loadingEvents">
                          <td colspan="4" class="loading-events">
                            <i class="fas fa-spinner fa-spin"></i>
                            {{ $ml.get('loading_events') }}
                          </td>
                        </tr>
                        <template v-else-if="!loadingEvents && getPaginatedEvents().length === 0">
                          <tr>
                            <td colspan="4" class="no-events">
                              {{ $ml.get('no_events') }}
                            </td>
                          </tr>
                        </template>
                        <template v-else>
                          <tr
                            v-for="(event, index) in getPaginatedEvents()"
                            :key="event.id || event._id || `event-${index}`"
                            class="event-row event-row-clickable"
                            :class="{ 'event-row-selected': isEventRowSelected(event, index) }"
                            @click="onEventRowClick(event, index)"
                            @mouseenter="onRowHover(event)"
                            @mouseleave="onRowLeave()"
                          >
                            <td>{{ getEventNumber(index) }}</td>
                            <td>{{ formatEventDate(event) }}</td>
                            <td>{{ formatEventEndForRow(event, index) }}</td>
                            <td>
                              <a
                                v-if="getEvidenceUrl(event)"
                                :href="getEvidenceUrl(event)"
                                target="_blank"
                                class="evidence-link"
                                @click.stop
                              >
                                <span class="mdi mdi-image"></span> Ver
                              </a>
                              <span v-else>-</span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="!loadingEvents && getEventsList().length > 0" class="table-pagination">
                  <div class="pagination-info">
                    {{ getShowingEventsRangeText() }}
                  </div>
                  <div class="pagination-controls">
                    <button
                      class="btn-pagination"
                      @click="previousPage"
                      :disabled="currentPage === 1"
                      :title="$ml.get('previous')"
                    >
                      <span class="mdi mdi-chevron-left"></span>
                    </button>
<span class="page-info">
                    {{ getPageOfText() }}
                  </span>
                    <button
                      class="btn-pagination"
                      @click="nextPage"
                      :disabled="currentPage >= totalPages"
                      :title="$ml.get('next')"
                    >
                      <span class="mdi mdi-chevron-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Detail Event Information: #, FECHA, CATEGORÍA, TAG, EVIDENCIA -->
            <div class="events-table-block">
              <h3>{{ $ml.get('detail_event_information') }}</h3>
              <div class="table-container">
                <div class="table-wrapper">
                  <table class="events-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $ml.get('date') }}</th>
                        <th>{{ $ml.get('category') }}</th>
                        <th>{{ $ml.get('tag') }}</th>
                        <th>{{ $ml.get('evidence') }}</th>
                        <th>{{ $ml.get('event_source') }}</th>
                      </tr>
                    </thead>
                  </table>
                  <div class="table-body-scroll">
                    <table class="events-table">
                      <tbody>
                        <tr v-if="!selectedEventRange">
                          <td colspan="6" class="no-events">
                            {{ $ml.get('click_row_to_show_events') }}
                          </td>
                        </tr>
                        <tr v-else-if="loadingDetailEvents" class="loading-events">
                          <td colspan="6">
                            <i class="fas fa-spinner fa-spin"></i>
                            {{ $ml.get('loading_events') }}
                          </td>
                        </tr>
                        <template v-else-if="getDetailEventsList().length === 0">
                          <tr>
                            <td colspan="6" class="no-events">
                              {{ $ml.get('no_events_in_range') }}
                            </td>
                          </tr>
                        </template>
                        <template v-else>
                          <tr
                            v-for="(ev, idx) in getDetailEventsList()"
                            :key="ev.id || ev._id || `detail-${idx}`"
                            class="event-row"
                            @mouseenter="onRowHover(ev)"
                            @mouseleave="onRowLeave()"
                          >
                            <td>{{ idx + 1 }}</td>
                            <td>{{ formatEventDate(ev) }}</td>
                            <td>{{ getDetailEventCategory(ev) }}</td>
                            <td>{{ getDetailEventTag(ev) }}</td>
                            <td>
                              <a
                                v-if="getEvidenceUrl(ev)"
                                :href="getEvidenceUrl(ev)"
                                target="_blank"
                                class="evidence-link"
                              >
                                <span class="mdi mdi-image"></span> {{ $ml.get('view') }}
                              </a>
                              <span v-else>-</span>
                            </td>
                            <td>{{ getDetailEventSource(ev) }}</td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="eventType === 'axle'">
            <div class="events-table-single">
              <h3>{{ $ml.get('event_information') }}</h3>
              <div class="table-container">
                <div class="table-wrapper">
                  <table class="events-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ $ml.get('date') }}</th>
                        <th>{{ $ml.get('vehicle_type') }}</th>
                        <th>{{ $ml.get('axle_count') }}</th>
                        <th>{{ $ml.get('evidence') }}</th>
                      </tr>
                    </thead>
                  </table>
                  <div class="table-body-scroll">
                    <table class="events-table">
                      <tbody>
                        <tr v-if="loadingEvents">
                          <td colspan="5" class="loading-events">
                            <i class="fas fa-spinner fa-spin"></i>
                            {{ $ml.get('loading_events') }}
                          </td>
                        </tr>
                        <template v-else-if="!loadingEvents && getPaginatedEvents().length === 0">
                          <tr>
                            <td colspan="5" class="no-events">
                              {{ $ml.get('no_events') }}
                            </td>
                          </tr>
                        </template>
                        <template v-else>
                          <tr
                            v-for="(event, index) in getPaginatedEvents()"
                            :key="event.id || event._id || `event-${index}`"
                            @mouseenter="onRowHover(event)"
                            @mouseleave="onRowLeave()"
                            class="event-row"
                          >
                            <td>{{ getEventNumber(index) }}</td>
                            <td>{{ formatEventDate(event) }}</td>
                            <td>{{ event.vehicle_type || '-' }}</td>
                            <td>{{ event.axle_count != null ? event.axle_count : '-' }}</td>
                            <td>
                              <a
                                v-if="getEvidenceUrl(event)"
                                :href="getEvidenceUrl(event)"
                                target="_blank"
                                class="evidence-link"
                              >
                                <span class="mdi mdi-image"></span> {{ $ml.get('view') }}
                              </a>
                              <span v-else>-</span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="!loadingEvents && getEventsList().length > 0" class="table-pagination">
                  <div class="pagination-info">
                    {{ getShowingEventsRangeText() }}
                  </div>
                  <div class="pagination-controls">
                    <button
                      class="btn-pagination"
                      @click="previousPage"
                      :disabled="currentPage === 1"
                      :title="$ml.get('previous')"
                    >
                      <span class="mdi mdi-chevron-left"></span>
                    </button>
                    <span class="page-info">{{ getPageOfText() }}</span>
                    <button
                      class="btn-pagination"
                      @click="nextPage"
                      :disabled="currentPage >= totalPages"
                      :title="$ml.get('next')"
                    >
                      <span class="mdi mdi-chevron-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="events-table-single">
              <h3>{{ $ml.get('event_information') }}</h3>
              <div class="table-container">
              <div class="table-wrapper">
                <table class="events-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{{ $ml.get('date') }}</th>
                      <th>{{ $ml.get('category') }}</th>
                      <th>{{ $ml.get('tag') }}</th>
                      <th>{{ $ml.get('evidence') }}</th>
                    </tr>
                  </thead>
                </table>
                <div class="table-body-scroll">
                  <table class="events-table">
                    <tbody>
                      <tr v-if="loadingEvents">
                        <td colspan="5" class="loading-events">
                          <i class="fas fa-spinner fa-spin"></i>
                          {{ $ml.get('loading_events') }}
                        </td>
                      </tr>
                      <template v-else-if="!loadingEvents && getPaginatedEvents().length === 0">
                        <tr>
                          <td colspan="5" class="no-events">
                            {{ $ml.get('no_events') }}
                          </td>
                        </tr>
                      </template>
                      <template v-else>
                          <tr
                            v-for="(event, index) in getPaginatedEvents()"
                            :key="event.id || event._id || `event-${index}`"
                            @mouseenter="onRowHover(event)"
                            @mouseleave="onRowLeave()"
                            class="event-row"
                          >
                            <td>{{ getEventNumber(index) }}</td>
                            <td>{{ formatEventDate(event) }}</td>
                            <td>{{ event.motive_categorie || '-' }}</td>
                            <td>{{ event.tag || '-' }}</td>
                            <td>
                              <a
                                v-if="getEvidenceUrl(event)"
                                :href="getEvidenceUrl(event)"
                                target="_blank"
                                class="evidence-link"
                              >
                                <span class="mdi mdi-image"></span> {{ $ml.get('view') }}
                              </a>
                              <span v-else>-</span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-if="!loadingEvents && getEventsList().length > 0" class="table-pagination">
                  <div class="pagination-info">
                    {{ getShowingEventsRangeText() }}
                  </div>
                  <div class="pagination-controls">
                    <button
                      class="btn-pagination"
                      @click="previousPage"
                      :disabled="currentPage === 1"
                      :title="$ml.get('previous')"
                    >
                      <span class="mdi mdi-chevron-left"></span>
                    </button>
<span class="page-info">
                    {{ getPageOfText() }}
                  </span>
                    <button
                      class="btn-pagination"
                      @click="nextPage"
                      :disabled="currentPage >= totalPages"
                      :title="$ml.get('next')"
                    >
                      <span class="mdi mdi-chevron-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Footer del Modal -->
      <div class="modal-footer">
        <button 
          class="btn btn-nav"
          @click="navigateToPrevious"
          :disabled="!canNavigatePrevious"
          :title="$ml.get('previous_event')"
        >
          <span class="mdi mdi-chevron-left"></span>
          {{ $ml.get('previous') }}
        </button>
        
        <span class="navigation-info">
          {{ currentIndex + 1 }} / {{ totalEvents }}
        </span>
        
        <button 
          class="btn btn-nav"
          @click="navigateToNext"
          :disabled="!canNavigateNext"
          :title="$ml.get('next_event') || 'Siguiente Evento'"
        >
          {{ $ml.get('next') }}
          <span class="mdi mdi-chevron-right"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import './AlertSmartEventModal.css';
import component from './AlertSmartEventModal.js';
export default component;
</script>
